import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";

interface AIAssistantContextType {
  isAIAssistantOpen: boolean;
  setIsAIAssistantOpen: (open: boolean) => void;
  currentOrganizationId: string | null;
  currentOrganizationName: string | null;
  setCurrentOrganization: (id: string | null, name: string | null) => void;
}

const AIAssistantContext = createContext<AIAssistantContextType | undefined>(undefined);

export const AIAssistantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
  const [currentOrganizationId, setCurrentOrganizationId] = useState<string | null>(null);
  const [currentOrganizationName, setCurrentOrganizationName] = useState<string | null>(null);
  const location = useLocation();
  const { organizationId } = useParams<{ organizationId: string }>();

  // Auto-detect organization from URL
  useEffect(() => {
    if (location.pathname.includes('/organizations/') && organizationId) {
      // Try to get organization name from localStorage cache
      const cachedOrgName = localStorage.getItem(`org_name_${organizationId}`);
      if (cachedOrgName) {
        setCurrentOrganizationName(cachedOrgName);
      }
      setCurrentOrganizationId(organizationId);
    } else {
      // Keep the current organization info even when navigating away
      // This allows the AI Assistant to continue working with the cached organization
    }
  }, [location.pathname, organizationId]);

  const setCurrentOrganization = (id: string | null, name: string | null) => {
    setCurrentOrganizationId(id);
    setCurrentOrganizationName(name);
    
    // Cache organization name
    if (id && name) {
      localStorage.setItem(`org_name_${id}`, name);
    }
  };

  return (
    <AIAssistantContext.Provider
      value={{
        isAIAssistantOpen,
        setIsAIAssistantOpen,
        currentOrganizationId,
        currentOrganizationName,
        setCurrentOrganization,
      }}
    >
      {children}
    </AIAssistantContext.Provider>
  );
};

export const useAIAssistant = () => {
  const context = useContext(AIAssistantContext);
  if (context === undefined) {
    throw new Error("useAIAssistant must be used within an AIAssistantProvider");
  }
  return context;
};
